import React, { useState, useEffect } from 'react';
import { DollarSign, Calendar, Plus, Trash2, RefreshCw, Repeat, Users } from 'lucide-react';
import { forecastService, Expense, ExpenseCategory, ExpenseAllocation } from '../../lib/forecastService';
import { useBrand } from '../../contexts/BrandContext';
import { useToast } from '../../contexts/ToastContext';

interface ExpenseListProps {
  onRefresh: () => void;
}

const ExpenseList: React.FC<ExpenseListProps> = ({ onRefresh }) => {
  const { currentBrand, brands } = useBrand();
  const { showToast } = useToast();
  const [expenses, setExpenses] = useState<Expense[]>([]);
  const [categories, setCategories] = useState<ExpenseCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    expense_name: '',
    amount: '',
    expense_type: 'one_time' as 'one_time' | 'recurring',
    expense_date: new Date().toISOString().split('T')[0],
    recurrence_day: '1',
    recurrence_start_date: new Date().toISOString().split('T')[0],
    category_id: '',
  });
  const [allocations, setAllocations] = useState<{ brand_id: string; allocation_percentage: string }[]>([{ brand_id: currentBrand?.id || '', allocation_percentage: '100' }]);

  useEffect(() => {
    if (currentBrand) {
      loadData();
      setAllocations([{ brand_id: currentBrand.id, allocation_percentage: '100' }]);
    }
  }, [currentBrand]);

  const loadData = async () => {
    if (!currentBrand) return;

    try {
      setLoading(true);
      const [expensesData, categoriesData] = await Promise.all([
        forecastService.getExpensesForBrand(currentBrand.id),
        forecastService.getExpenseCategories(currentBrand.id),
      ]);
      setExpenses(expensesData);
      setCategories(categoriesData);
    } catch (error) {
      showToast('Failed to load expenses', 'error');
      console.error('Error loading expenses:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!currentBrand) return;

    const totalAllocation = allocations.reduce((sum, alloc) => sum + parseFloat(alloc.allocation_percentage || '0'), 0);
    if (Math.abs(totalAllocation - 100) > 0.01) {
      showToast('Total allocation must equal 100%', 'error');
      return;
    }

    try {
      await forecastService.createExpenseWithAllocations({
        brand_id: currentBrand.id,
        expense_name: formData.expense_name,
        amount: parseFloat(formData.amount),
        expense_type: formData.expense_type,
        expense_date: formData.expense_type === 'one_time' ? formData.expense_date : undefined,
        recurrence_day: formData.expense_type === 'recurring' ? parseInt(formData.recurrence_day) : undefined,
        recurrence_start_date: formData.expense_type === 'recurring' ? formData.recurrence_start_date : undefined,
        status: 'active',
        payment_status: 'pending',
        category_id: formData.category_id || undefined,
        is_shared: allocations.length > 1,
      }, allocations.map(a => ({
        brand_id: a.brand_id,
        allocation_percentage: parseFloat(a.allocation_percentage)
      })));
      showToast('Expense added successfully', 'success');
      setShowForm(false);
      setFormData({
        expense_name: '',
        amount: '',
        expense_type: 'one_time',
        expense_date: new Date().toISOString().split('T')[0],
        recurrence_day: '1',
        recurrence_start_date: new Date().toISOString().split('T')[0],
        category_id: '',
      });
      setAllocations([{ brand_id: currentBrand.id, allocation_percentage: '100' }]);
      loadData();
      onRefresh();
    } catch (error) {
      showToast('Failed to add expense', 'error');
      console.error('Error adding expense:', error);
    }
  };

  const handleDelete = async (expenseId: string) => {
    if (!confirm('Are you sure you want to delete this expense?')) return;

    try {
      await forecastService.deleteExpense(expenseId);
      showToast('Expense deleted successfully', 'success');
      loadData();
      onRefresh();
    } catch (error) {
      showToast('Failed to delete expense', 'error');
      console.error('Error deleting expense:', error);
    }
  };

  const getCategoryName = (categoryId?: string) => {
    if (!categoryId) return 'Uncategorized';
    const category = categories.find(c => c.id === categoryId);
    return category?.name || 'Unknown';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-gray-500">Loading expenses...</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-gray-900">Expenses</h3>
        <button
          onClick={() => setShowForm(!showForm)}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          <Plus className="w-4 h-4" />
          Add Expense
        </button>
      </div>

      {showForm && (
        <form onSubmit={handleSubmit} className="bg-white border border-gray-200 rounded-lg p-4 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Expense Name *</label>
              <input
                type="text"
                required
                value={formData.expense_name}
                onChange={(e) => setFormData({ ...formData, expense_name: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Office rent, Software subscription, etc."
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Amount *</label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                <input
                  type="number"
                  required
                  min="0"
                  step="0.01"
                  value={formData.amount}
                  onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                  className="w-full pl-8 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="1000.00"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Type *</label>
              <select
                value={formData.expense_type}
                onChange={(e) => setFormData({ ...formData, expense_type: e.target.value as any })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="one_time">One-Time</option>
                <option value="recurring">Recurring</option>
              </select>
            </div>

            {formData.expense_type === 'one_time' ? (
              <div className="col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Expense Date *</label>
                <input
                  type="date"
                  required
                  value={formData.expense_date}
                  onChange={(e) => setFormData({ ...formData, expense_date: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            ) : (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Billing Day *</label>
                  <select
                    value={formData.recurrence_day}
                    onChange={(e) => setFormData({ ...formData, recurrence_day: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    {Array.from({ length: 31 }, (_, i) => i + 1).map((day) => (
                      <option key={day} value={day}>{day}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Start Date *</label>
                  <input
                    type="date"
                    required
                    value={formData.recurrence_start_date}
                    onChange={(e) => setFormData({ ...formData, recurrence_start_date: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </>
            )}

            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
              <select
                value={formData.category_id}
                onChange={(e) => setFormData({ ...formData, category_id: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">Select category</option>
                {categories.map((cat) => (
                  <option key={cat.id} value={cat.id}>{cat.name}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="border-t border-gray-200 pt-4">
            <div className="flex items-center justify-between mb-3">
              <label className="block text-sm font-medium text-gray-700">Brand Allocation</label>
              {allocations.length < 3 && (
                <button
                  type="button"
                  onClick={() => {
                    const availableBrands = brands.filter(b => !allocations.some(a => a.brand_id === b.id));
                    if (availableBrands.length > 0) {
                      setAllocations([...allocations, { brand_id: availableBrands[0].id, allocation_percentage: '0' }]);
                    }
                  }}
                  className="text-xs text-blue-600 hover:text-blue-700 font-medium"
                >
                  + Split with another brand
                </button>
              )}
            </div>
            <div className="space-y-2">
              {allocations.map((alloc, index) => {
                const brand = brands.find(b => b.id === alloc.brand_id);
                return (
                  <div key={index} className="flex items-center gap-2">
                    <select
                      value={alloc.brand_id}
                      onChange={(e) => {
                        const newAllocations = [...allocations];
                        newAllocations[index].brand_id = e.target.value;
                        setAllocations(newAllocations);
                      }}
                      className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      {brands.map(b => (
                        <option key={b.id} value={b.id}>{b.name}</option>
                      ))}
                    </select>
                    <div className="relative w-24">
                      <input
                        type="number"
                        min="0"
                        max="100"
                        step="0.1"
                        value={alloc.allocation_percentage}
                        onChange={(e) => {
                          const newAllocations = [...allocations];
                          newAllocations[index].allocation_percentage = e.target.value;
                          setAllocations(newAllocations);
                        }}
                        className="w-full px-3 py-2 pr-7 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                      <span className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 text-xs">%</span>
                    </div>
                    {allocations.length > 1 && (
                      <button
                        type="button"
                        onClick={() => setAllocations(allocations.filter((_, i) => i !== index))}
                        className="p-2 text-red-600 hover:bg-red-50 rounded transition-colors"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                );
              })}
              <div className="flex justify-between items-center text-sm pt-2 border-t border-gray-200">
                <span className="text-gray-600">Total Allocation:</span>
                <span className={`font-medium ${
                  Math.abs(allocations.reduce((sum, a) => sum + parseFloat(a.allocation_percentage || '0'), 0) - 100) < 0.01
                    ? 'text-green-600'
                    : 'text-red-600'
                }`}>
                  {allocations.reduce((sum, a) => sum + parseFloat(a.allocation_percentage || '0'), 0).toFixed(1)}%
                </span>
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={() => setShowForm(false)}
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Add Expense
            </button>
          </div>
        </form>
      )}

      {expenses.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-lg border border-gray-200">
          <DollarSign className="w-12 h-12 text-gray-400 mx-auto mb-3" />
          <p className="text-gray-600 font-medium">No expenses yet</p>
          <p className="text-gray-500 text-sm mt-1">Add expenses to track your costs</p>
        </div>
      ) : (
        <div className="space-y-2">
          {expenses.map((expense) => (
            <div key={expense.id} className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-sm transition-shadow">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <h4 className="font-semibold text-gray-900">{expense.expense_name}</h4>
                    {expense.expense_type === 'recurring' && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-100 text-blue-800 text-xs rounded-full">
                        <Repeat className="w-3 h-3" />
                        Recurring
                      </span>
                    )}
                    {expense.is_shared && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-purple-100 text-purple-800 text-xs rounded-full">
                        <Users className="w-3 h-3" />
                        Shared
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-gray-600 mt-1">{getCategoryName(expense.category_id)}</p>
                  <div className="mt-2 flex items-center gap-4 text-sm">
                    <div className="flex items-center gap-1 font-semibold text-gray-900">
                      <DollarSign className="w-4 h-4" />
                      ${(expense.allocated_amount || expense.amount).toLocaleString('en-US', { minimumFractionDigits: 2 })}
                      {expense.allocated_amount && expense.allocated_amount !== expense.amount && (
                        <span className="text-gray-500 font-normal">
                          (${expense.amount.toLocaleString('en-US', { minimumFractionDigits: 2 })} total)
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-1 text-gray-600">
                      <Calendar className="w-4 h-4" />
                      {expense.expense_type === 'one_time'
                        ? new Date(expense.expense_date!).toLocaleDateString()
                        : `Day ${expense.recurrence_day} monthly`
                      }
                    </div>
                  </div>
                </div>
                <button
                  onClick={() => handleDelete(expense.id)}
                  className="p-2 text-gray-600 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                  title="Delete Expense"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ExpenseList;